import React from "react"
import { Typography, Space, Empty, Statistic, Row, Col } from "antd"
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons"
import { PatternMatch } from "./PatternMatch"
import { BlacklistPattern, MatchType, PatternMatchDetails } from "./types"

const { Text } = Typography

export interface PatternTestResultsProps {
  patterns: BlacklistPattern[]
  testString: string
}

const testPattern = (pattern: string, matchType: MatchType, testString: string): PatternMatchDetails | undefined => {
  if (!testString || !pattern) return undefined

  try {
    switch (matchType) {
    case MatchType.EXACT: {
      if (testString === pattern) {
        return {
          matchedText: testString,
          index: 0,
        }
      }
      break
    }

    case MatchType.PARTIAL: {
      const index = testString.toLowerCase().indexOf(pattern.toLowerCase())
      if (index !== -1) {
        return {
          matchedText: testString.substring(index, index + pattern.length),
          index,
        }
      }
      break
    }

    case MatchType.REGEX: {
      try {
        const regex = new RegExp(pattern, "gi")
        const match = regex.exec(testString)
        if (match) {
          return {
            matchedText: match[0],
            index: match.index,
            groups: match.slice(1).filter(Boolean),
          }
        }
      } catch (e) {
        // Invalid regex - will show no match
        return undefined
      }
      break
    }
    }
  } catch (e) {
    // Any error in pattern matching
    return undefined
  }

  return undefined
}

export const PatternTestResults: React.FC<PatternTestResultsProps> = ({
  patterns,
  testString,
}) => {
  if (!testString.trim()) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Enter a test string to see pattern matches"
        style={{ margin: "40px 0" }}
      />
    )
  }

  if (patterns.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No blacklist patterns to test"
        style={{ margin: "40px 0" }}
      />
    )
  }

  const results = patterns.map(({ pattern, matchType }) => {
    const matchDetails = testPattern(pattern, matchType, testString)
    const isMatch = !!matchDetails

    return {
      pattern,
      matchType,
      testString,
      isMatch,
      matchDetails,
    }
  })

  const matchCount = results.filter(r => r.isMatch).length
  const totalCount = results.length

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Statistic
            title="Total Patterns in Blacklist"
            value={totalCount}
            prefix={<CheckCircleOutlined />}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title="Matches Found"
            value={matchCount}
            valueStyle={{ color: matchCount > 0 ? "#52c41a" : "#8c8c8c" }}
            prefix={<CheckCircleOutlined />}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title="Would Block?"
            value={matchCount > 0 ? "YES" : "NO"}
            valueStyle={{
              color: matchCount > 0 ? "#f5222d" : "#52c41a",
              fontSize: 16,
              fontWeight: "bold",
            }}
            prefix={matchCount > 0 ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
          />
        </Col>
      </Row>

      {matchCount > 0 && (
        <div
          style={{
            padding: 12,
            backgroundColor: "#fff2e6",
            borderRadius: 6,
            border: "1px solid #ffd591",
            marginBottom: 16,
          }}
        >
          <Text style={{ color: "#d46b08" }}>
            ⚠️ This error would be <strong>blocked</strong> by your blacklist patterns and not sent to Sentry.
          </Text>
        </div>
      )}

      <div>
        <Text strong style={{ marginBottom: 8, display: "block" }}>
          Pattern Test Results:
        </Text>
        {results.map((result, index) => (
          <PatternMatch
            key={`${result.pattern}-${index}`}
            {...result}
          />
        ))}
      </div>
    </Space>
  )
}
